import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type RefCallback,
} from 'react'

export const CHART_XRAY_HANDLE_PX = 6

/** Allowed chart column width as % of split container (subject to pixel floors). */
export const CHART_XRAY_PANE_MIN_PCT = 30

export const CHART_XRAY_PANE_MAX_PCT = 70

/** Default chart column width when the x-ray panel is opened (50% chart / 50% viewer). */
export const CHART_XRAY_DEFAULT_CHART_FRACTION = 0.5

const MIN_CHART_PX = 280

const MIN_VIEWER_PX = 200

const MIN_CHART_FRACTION = CHART_XRAY_PANE_MIN_PCT / 100

const MAX_CHART_FRACTION = CHART_XRAY_PANE_MAX_PCT / 100

export const clampChartXrayFraction = (
  fraction: number,

  containerWidth: number,
): number => {
  if (containerWidth <= 0) {
    return CHART_XRAY_DEFAULT_CHART_FRACTION
  }

  const minF = Math.max(MIN_CHART_FRACTION, MIN_CHART_PX / containerWidth)

  const maxByViewer =
    (containerWidth - CHART_XRAY_HANDLE_PX - MIN_VIEWER_PX) / containerWidth

  const maxF = Math.min(MAX_CHART_FRACTION, maxByViewer)

  const low = Math.min(minF, maxF)
  const high = Math.max(minF, maxF)

  return Math.min(Math.max(fraction, low), high)
}

const applyDefaultChartFraction = (containerWidth: number): number =>
  clampChartXrayFraction(CHART_XRAY_DEFAULT_CHART_FRACTION, containerWidth)

export interface UseChartXraySplitResult {
  splitRef: RefCallback<HTMLDivElement | null>

  chartPaneFraction: number

  onHandleMouseDown: (event: React.MouseEvent<HTMLDivElement>) => void
}

/**

 * Horizontal split between chart column and X-ray viewer column (fraction of split container width).

 * Resets to 50/50 each time {@link isSplitActive} becomes true.

 */

export function useChartXraySplit(
  isSplitActive: boolean,
): UseChartXraySplitResult {
  const draggingRef = useRef(false)

  const fractionRef = useRef(CHART_XRAY_DEFAULT_CHART_FRACTION)

  const splitElementRef = useRef<HTMLDivElement | null>(null)

  const [chartPaneFraction, setChartPaneFraction] = useState(
    CHART_XRAY_DEFAULT_CHART_FRACTION,
  )

  const [splitMounted, setSplitMounted] = useState(false)

  useEffect(() => {
    fractionRef.current = chartPaneFraction
  }, [chartPaneFraction])

  /** Reset to 50/50 whenever the x-ray panel is toggled on. */
  useEffect(() => {
    if (!isSplitActive || !splitMounted || !splitElementRef.current) {
      return
    }

    const rect = splitElementRef.current.getBoundingClientRect()
    const next = applyDefaultChartFraction(rect.width)

    fractionRef.current = next
    setChartPaneFraction(next)
  }, [isSplitActive, splitMounted])

  useEffect(() => {
    const onMove = (event: MouseEvent): void => {
      if (!draggingRef.current || !splitElementRef.current) {
        return
      }

      const rect = splitElementRef.current.getBoundingClientRect()

      const raw = (event.clientX - rect.left) / rect.width

      const next = clampChartXrayFraction(raw, rect.width)

      fractionRef.current = next

      setChartPaneFraction(next)
    }

    const onUp = (): void => {
      if (!draggingRef.current) {
        return
      }

      draggingRef.current = false

      document.body.style.removeProperty('user-select')
    }

    window.addEventListener('mousemove', onMove)

    window.addEventListener('mouseup', onUp)

    return (): void => {
      window.removeEventListener('mousemove', onMove)

      window.removeEventListener('mouseup', onUp)
    }
  }, [])

  const onHandleMouseDown = useCallback(
    (event: React.MouseEvent<HTMLDivElement>): void => {
      event.preventDefault()

      draggingRef.current = true

      document.body.style.userSelect = 'none'

      if (splitElementRef.current) {
        const rect = splitElementRef.current.getBoundingClientRect()

        const raw = (event.clientX - rect.left) / rect.width

        const next = clampChartXrayFraction(raw, rect.width)

        fractionRef.current = next

        setChartPaneFraction(next)
      }
    },

    [],
  )

  const splitRef = useCallback(
    (node: HTMLDivElement | null) => {
      splitElementRef.current = node

      setSplitMounted(node !== null)
    },

    [],
  )

  useEffect(() => {
    if (!isSplitActive || !splitMounted || !splitElementRef.current) {
      return
    }

    const el = splitElementRef.current

    const ro = new ResizeObserver(() => {
      if (draggingRef.current) {
        return
      }

      const rect = el.getBoundingClientRect()

      const next = clampChartXrayFraction(fractionRef.current, rect.width)

      fractionRef.current = next

      setChartPaneFraction(next)
    })

    ro.observe(el)

    return (): void => {
      ro.disconnect()
    }
  }, [isSplitActive, splitMounted])

  return {
    splitRef,

    chartPaneFraction,

    onHandleMouseDown,
  }
}
